/**
 * Copy layer for the S0 chord-presentation view.
 *
 * Pure string composition: no React, no analyzer calls beyond reading the
 * fields already carried by `PhraseChord`. Kept separate from the view so
 * the wording — the part that actually decides whether S0 teaches
 * anything — is unit-testable and reviewable on its own.
 *
 * ## The rule that governs this whole file
 *
 * Chords come from an automatic analyzer running over user MIDI. It will
 * produce `Ebm7b5` and `C#dim/G`, and it will sometimes be wrong. An adult
 * beginner spots a hollow explanation instantly and then discounts
 * everything else the app says about the piece. So: **when we are not
 * sure, we say nothing.** Every helper here returns `null` rather than
 * hedging, and the view renders nothing for a `null`.
 */

import type { NoteNotation } from "@/lib/music/notation";
import { chordSymbolLabel, noteLabel } from "@/lib/music/notation";
import type { Tonality } from "@/lib/music/tonality";
import type { PhraseChord } from "./phrase-chords";

// ── Silence rule ──────────────────────────────────────────────────

/**
 * Qualities we are willing to make a harmonic claim about.
 *
 * Deliberately narrower than `QUALITY_INTERVALS` in `phrase-chords.ts`:
 * that map exists to *derive geometry* (bass, inversion, missing degrees)
 * and can afford to cover exotic shapes. This set gates *prose*, and
 * anything outside it renders as symbol + concrete notes only.
 */
const GLOSSABLE_QUALITIES = new Set([
  "major",
  "minor",
  "m",
  "7",
  "dominant7",
  "maj7",
  "m7",
  "min7",
  "sus2",
  "sus4",
]);

/** True when we may attach a degree gloss / explanation to this chord. */
export function isGlossable(entry: PhraseChord): boolean {
  return (
    entry.qualityKnown &&
    GLOSSABLE_QUALITIES.has(entry.chord.quality) &&
    entry.chord.romanNumeral !== ""
  );
}

// ── Chord prose ───────────────────────────────────────────────────

/** Quality rendered as a French word, for running prose only. */
const QUALITY_PROSE: Readonly<Record<string, string>> = {
  major: "majeur",
  minor: "mineur",
  m: "mineur",
  diminished: "diminué",
  augmented: "augmenté",
  sus2: "sus2",
  sus4: "sus4",
  "7": "7",
  dominant7: "7",
  maj7: "majeur 7",
  m7: "mineur 7",
  min7: "mineur 7",
  "6": "6",
  maj6: "6",
  m6: "mineur 6",
  min6: "mineur 6",
  dim7: "diminué 7",
  aug7: "augmenté 7",
  minmaj7: "mineur majeur 7",
  mM7: "mineur majeur 7",
  "5": "quinte",
  power: "quinte",
};

/**
 * Le suffixe conventionnel de chaque qualité, tel qu'une grille l'écrit.
 *
 * ## Le majeur ne s'écrit pas
 *
 * C'est **la** règle du chiffrage, anglo-saxon comme latin : `Ré` est un accord
 * de ré majeur, `Ré m` un ré mineur. Le cas le plus fréquent est celui qu'on
 * n'écrit pas, et c'est ce qui rend la grille lisible d'un coup d'œil : ce
 * qu'on lit est toujours ce qui **s'écarte** du majeur.
 *
 * Son prix est réel et il faut le connaître : un débutant ne peut pas
 * distinguer « majeur » de « on ne l'a pas dit » tant qu'il n'a pas appris la
 * convention. C'est pour ça que le chiffrage ne remplace jamais la prose : il
 * vit sur la partition, où la place manque, pendant que le nom en toutes
 * lettres reste en grand au milieu de l'écran. Les deux à la fois, c'est ce qui
 * fait apprendre la convention au lieu de la supposer connue.
 *
 * ## `m` et non « min. »
 *
 * `m` est le signe qu'emploient les grilles, les recueils et les tutoriels ;
 * « min. » est une abréviation de mot, donc de la prose raccourcie, qui ne se
 * trouve nulle part sur une grille. Écrire le signe est ce qui relie le joueur
 * à tout ce qui existe en dehors de Virtuosa.
 */
const QUALITY_SHORT: Readonly<Record<string, string>> = {
  major: "",
  minor: "m",
  m: "m",
  diminished: "dim",
  augmented: "aug",
  sus2: "sus2",
  sus4: "sus4",
  "7": "7",
  dominant7: "7",
  maj7: "maj7",
  m7: "m7",
  min7: "m7",
  "6": "6",
  maj6: "6",
  m6: "m6",
  min6: "m6",
  dim7: "dim7",
  aug7: "aug7",
  minmaj7: "mM7",
  mM7: "mM7",
  "5": "5",
  power: "5",
};

/**
 * L'accord en chiffrage, dans la notation du joueur : `"Ré m"`, `"Mi♭"`.
 *
 * C'est ce que `chordProseLabel` annonce déjà : le symbole compact vit sur la
 * frise, où il est une étiquette et non une phrase. Une portée écrit une grille
 * au-dessus d'elle, et une grille ne fait jamais de phrase.
 *
 * Dans la notation des **notes** et non dans celle des symboles d'accord : il
 * se lit à quinze pixels des notes qu'il liste, et deux systèmes de noms dans
 * un seul bloc feraient douter des deux.
 *
 * La basse est omise, comme dans la prose : ici c'est la portée de la main
 * gauche qui la dessine et qui la nomme, à sa hauteur, une ligne plus bas.
 */
export function chordShortLabel(
  entry: { readonly chord: Pick<PhraseChord["chord"], "root" | "quality"> },
  notation: NoteNotation,
): string {
  const suffix = QUALITY_SHORT[entry.chord.quality] ?? entry.chord.quality;

  // Recomposé puis rendu par `chordSymbolLabel` plutôt qu'assemblé ici : c'est
  // lui qui sait mettre les glyphes d'altération et l'espace insécable qui
  // empêche `Do m` de se lire « Dom ».
  return chordSymbolLabel(`${entry.chord.root}${suffix}`, notation);
}

/**
 * The two halves of the spelled-out chord: the note, then what is built on it.
 *
 * Split rather than concatenated because the caller sometimes has to treat
 * them differently — the harmony scene tints the root and leaves the quality
 * white — and because splitting the finished string is not safe: `"Si bémol
 * majeur"` has three words and only the first two are the note.
 *
 * `quality` is `null` for a shape we have no French word for, in which case
 * the chord is named by its root alone.
 */
export type ChordProse = {
  readonly root: string;
  readonly quality: string | null;
};

export function chordProseParts(
  /**
   * Structural rather than a whole `PhraseChord`: the grip step names the
   * chord it holds part of, and it has the analyzer's chord without the
   * frieze's window around it.
   */
  entry: { readonly chord: Pick<PhraseChord["chord"], "root" | "quality"> },
  notation: NoteNotation,
): ChordProse {
  return {
    root: noteLabel(entry.chord.root, notation),
    quality: QUALITY_PROSE[entry.chord.quality] ?? null,
  };
}

/**
 * Chord spelled out in words: `"Mi mineur"`, `"Sol majeur"`.
 *
 * Prose never uses the compact symbol (`Em`, `G/D`). Reading `Em` aloud
 * requires knowing the english letter convention — exactly the barrier
 * Virtuosa removes elsewhere. The compact symbol stays in the frieze,
 * where it is a label rather than a sentence.
 *
 * The bass is intentionally omitted: `chordProseLabel` answers "which
 * chord", and the inversion gets its own sentence in `focusLine`.
 */
export function chordProseLabel(
  entry: { readonly chord: Pick<PhraseChord["chord"], "root" | "quality"> },
  notation: NoteNotation,
): string {
  const { root, quality } = chordProseParts(entry, notation);

  return quality === null ? root : `${root} ${quality}`;
}

/** Bass note name, or `null` when the bass is the root (or unknown). */
export function bassNoteLabel(
  entry: PhraseChord,
  notation: NoteNotation,
): string | null {
  if (entry.bassMidi === null || entry.inversion === 0) {
    return null;
  }

  const bassPc = ((entry.bassMidi % 12) + 12) % 12;

  return noteLabel(PITCH_CLASS_NAMES[bassPc], notation);
}

const PITCH_CLASS_NAMES = [
  "C",
  "C#",
  "D",
  "D#",
  "E",
  "F",
  "F#",
  "G",
  "G#",
  "A",
  "A#",
  "B",
];

// ── Degree glosses ────────────────────────────────────────────────

/**
 * What a degree *feels* like, in words the listener already owns.
 *
 * These are the primary label; the roman numeral is the footnote. A roman
 * numeral encodes a relation to a tonality — a second-order abstraction
 * that assumes the tonality is already a stable referent. It isn't yet,
 * for a beginner. "Celui qui appelle la suite" describes a sensation they
 * have had a thousand times without naming it.
 *
 * These are plain words, not jargon: they get no `Term` tooltip.
 */
const DEGREE_GLOSS_MAJOR: Readonly<Record<string, string>> = {
  I: "le point d'appui",
  V: "celui qui appelle la suite",
  V7: "celui qui appelle la suite",
  IV: "celui qui ouvre",
  vi: "le versant sombre",
  ii: "celui qui prépare",
};

const DEGREE_GLOSS_MINOR: Readonly<Record<string, string>> = {
  i: "le point d'appui",
  V: "celui qui appelle la suite",
  V7: "celui qui appelle la suite",
  iv: "celui qui ouvre",
  VI: "l'adoucissement",
  III: "l'éclaircie",
  VII: "celui qui pousse en avant",
};

/** Degree gloss for a chord, or `null` when we should stay silent. */
export function degreeGloss(
  entry: PhraseChord,
  tonality: Tonality | null,
): string | null {
  if (tonality === null || !isGlossable(entry)) {
    return null;
  }

  const table =
    tonality.mode === "minor" ? DEGREE_GLOSS_MINOR : DEGREE_GLOSS_MAJOR;

  return table[entry.chord.romanNumeral] ?? null;
}

// ── Phrase hook ───────────────────────────────────────────────────

/** How the phrase's last chord leaves the listener. */
const ARRIVAL_GLOSS: Readonly<Record<string, string>> = {
  I: "posé, ça retombe.",
  i: "posé, ça retombe.",
  V: "en suspens, ça appelle la suite.",
  V7: "en suspens, ça appelle la suite.",
  IV: "ouvert, ça continue.",
  iv: "ouvert, ça continue.",
  vi: "en demi-teinte, ça continue.",
  VI: "en demi-teinte, ça continue.",
  III: "lumineux, ça continue.",
  VII: "ça pousse en avant.",
};

/** Above this many chords, the phrase is moving too fast to narrate. */
const BUSY_CHORD_COUNT = 6;

/**
 * The one-line hook above the frieze.
 *
 * Built from three facts only (first chord, last chord, arrival degree),
 * so it stays honest on any input. Each fallback below is a real case
 * seen on user MIDI, not defensive padding.
 */
export function phraseHook(
  phraseChords: readonly PhraseChord[],
  tonality: Tonality | null,
  measureCount: number,
  notation: NoteNotation,
): string | null {
  if (phraseChords.length === 0) {
    return "Cette phrase est une ligne de notes, jouées une par une.";
  }

  if (phraseChords.length === 1) {
    const only = chordProseLabel(phraseChords[0], notation);

    return `Toute la phrase tient sur un seul accord : ${only}.`;
  }

  if (phraseChords.length > BUSY_CHORD_COUNT) {
    return `Ça bouge vite : ${phraseChords.length} accords en ${measureCount} mesures.`;
  }

  const first = chordProseLabel(phraseChords[0], notation);
  const lastEntry = phraseChords[phraseChords.length - 1];
  const last = chordProseLabel(lastEntry, notation);
  const arrival = arrivalGloss(lastEntry, tonality);

  if (arrival === null) {
    return `Ça part de ${first} et ça finit sur ${last}.`;
  }

  return `Ça part de ${first} et ça finit sur ${last} : ${arrival}`;
}

function arrivalGloss(
  entry: PhraseChord,
  tonality: Tonality | null,
): string | null {
  if (tonality === null || !isGlossable(entry)) {
    return null;
  }

  return ARRIVAL_GLOSS[entry.chord.romanNumeral] ?? null;
}

// ── Lexicon ───────────────────────────────────────────────────────

/**
 * Terms surfaced as `Term` (dotted underline + tooltip).
 *
 * Each definition is one sentence, assumes zero music theory, and says
 * what the thing *does* for the player rather than what it is in the
 * abstract.
 */
export const S0_TERMS: Readonly<Record<string, string>> = {
  renversé:
    "L'accord contient les mêmes notes, mais ce n'est pas sa note principale qui est en bas. Ça change sa couleur et ça évite à ta main de sauter d'un accord à l'autre.",
  basse:
    "La note la plus grave de l'accord, celle que tu entends « en dessous ». C'est elle qui donne l'assise.",
  tierce:
    "La note du milieu de l'accord. C'est elle, et elle seule, qui dit si l'accord sonne majeur (clair) ou mineur (sombre).",
  quinte:
    "La note du haut de l'accord de base. Elle ne change pas la couleur, elle épaissit le son.",
  degré:
    "La place d'un accord dans la tonalité, notée en chiffres romains (I, IV, V…). C'est ce qui te permet de dire « cet enchaînement, je le connais » même dans un autre morceau.",
  tonalité:
    "Le « chez-soi » du morceau : la note et la couleur autour desquelles tout tourne.",
  suspension:
    "Une note volontairement posée juste à côté de celle qu'on attend. Ça tire un peu, et ça se dénoue à l'accord suivant. C'est fait exprès.",
  arpégé:
    "Les notes de l'accord ne sont pas jouées ensemble mais l'une après l'autre, comme un escalier.",
  "note tenue":
    "Tu n'appuies pas à nouveau : le doigt reste enfoncé depuis l'accord d'avant.",
  "accord incomplet":
    "Toutes les notes de l'accord ne sont pas jouées. L'oreille complète toute seule, c'est très courant, et ça allège la main.",
};

// ── Focus line ────────────────────────────────────────────────────

/**
 * A single explanation line, as a template split around one glossary
 * term so the view can wrap that word in a `Term`.
 */
export type FocusLine = {
  readonly before: string;
  readonly term: string;
  readonly after: string;
};

/**
 * The one line under the focus keyboard — at most one, by priority.
 *
 * Priority order is by pedagogical payload, not by how interesting the
 * fact is: inversion changes what the *hand does*, a missing third
 * changes what the chord *sounds like*, the rest is context.
 *
 * Returning `null` is the common case and it is deliberate. A line that
 * appears under every chord becomes wallpaper within three phrases, and
 * then the one that mattered goes unread too.
 */
export function focusLine(
  entry: PhraseChord,
  notation: NoteNotation,
): FocusLine | null {
  if (!isGlossable(entry)) {
    return null;
  }

  const bass = bassNoteLabel(entry, notation);

  if (bass !== null) {
    const root = noteLabel(entry.chord.root, notation);

    return {
      before: `La note la plus grave n'est pas ${root}, c'est ${bass}. On dit que l'accord est `,
      term: "renversé",
      after:
        " : ta main gauche glisse au lieu de sauter, et la basse descend tout doucement d'un accord à l'autre.",
    };
  }

  if (entry.missingThird) {
    return {
      before: "La ",
      term: "tierce",
      after:
        " n'est pas jouée ici. C'est elle qui dit « majeur » ou « mineur » : sans elle, l'accord sonne ouvert, un peu creux, moins tranché.",
    };
  }

  if (entry.missingFifth) {
    const root = noteLabel(entry.chord.root, notation);

    return {
      before: "",
      term: "accord incomplet",
      after: ` : on garde ${root} et la note qui donne la couleur. C'est suffisant, l'oreille complète le reste.`,
    };
  }

  if (entry.chord.hasSuspension !== undefined) {
    return {
      before: "Une note est posée juste à côté de sa voisine, c'est une ",
      term: "suspension",
      after: ". Ça gratte un peu, et ça se dénoue à l'accord suivant.",
    };
  }

  // How the notes are laid out in time — plaqué, arpégé — is not here any
  // more: it is said on every chord by `passageLine`, and repeating it as the
  // one explanation would say it twice.
  return null;
}

// ── Texture ───────────────────────────────────────────────────────

/**
 * How the chord is played, in words: `"arpégé du grave à l'aigu"`.
 *
 * Read off the analyzer's voicing, which says both the layout (plaqué,
 * arpégé) and, for an arpeggio, which way it goes. The direction is only
 * said when the analyzer states one; `mixed` says nothing, since the two
 * hands are doing different things and one word would be true of one of
 * them only.
 */
export function chordTexture(entry: PhraseChord): string | null {
  const { type, direction } = entry.chord.voicing;

  if (type === "block") {
    return "plaqué";
  }

  if (type === "arpeggio" || type === "broken") {
    return ARPEGGIO_DIRECTION[direction ?? ""] ?? "arpégé";
  }

  if (type === "melodic" || type === "single") {
    return "une ligne, une note après l'autre";
  }

  return null;
}

const ARPEGGIO_DIRECTION: Readonly<Record<string, string>> = {
  ascending: "arpégé du grave à l'aigu",
  descending: "arpégé de l'aigu au grave",
  "ascending-descending": "arpégé, en montant puis en redescendant",
  "descending-ascending": "arpégé, en descendant puis en remontant",
};

/**
 * What the passage under this chord is: `"Arpégé du grave à l'aigu · 1
 * mesure"`. `null` when neither half is known.
 */
export function passageLine(
  entry: PhraseChord,
  ticksPerBeat: number,
  ticksPerBar: number,
): string | null {
  const parts = [
    chordTexture(entry),
    chordDurationLabel(entry, ticksPerBeat, ticksPerBar),
  ].filter((part): part is string => part !== null);

  if (parts.length === 0) {
    return null;
  }

  const line = parts.join(" · ");

  return line.charAt(0).toUpperCase() + line.slice(1);
}

// ── Duration ──────────────────────────────────────────────────────

/**
 * How long the chord lasts, in words: `"2 mesures"`, `"3 temps"`.
 *
 * This is the *numeric* channel for duration. The rail under the frieze
 * shows the proportions at a glance; this puts an exact value on the one
 * chord being looked at, so nothing has to be inferred from pixel widths.
 *
 * A clipped chord is qualified rather than silently mis-stated: the span
 * we can measure is the part inside the phrase, not the chord's real life.
 */
export function chordDurationLabel(
  entry: PhraseChord,
  ticksPerBeat: number,
  ticksPerBar: number,
): string | null {
  if (ticksPerBeat <= 0 || entry.spanTicks <= 0) {
    return null;
  }

  const clipped = entry.clippedStart || entry.clippedEnd;
  const suffix = clipped ? " (déborde la phrase)" : "";

  if (ticksPerBar > 0 && entry.spanTicks % ticksPerBar === 0) {
    const bars = entry.spanTicks / ticksPerBar;

    return `${bars} mesure${bars > 1 ? "s" : ""}${suffix}`;
  }

  const beats = Math.round((entry.spanTicks / ticksPerBeat) * 2) / 2;

  if (beats <= 0) {
    return null;
  }

  return `${beats} temps${suffix}`;
}

// ── Note names ────────────────────────────────────────────────────

/**
 * Past this many distinct note names, the list stops describing a chord.
 *
 * A quality we are willing to name carries four tones at most (see
 * `QUALITY_INTERVALS`), and a melodic hand crossing the window adds a
 * passing note or two. Beyond that the list is the window's scale material,
 * not its harmony, and enumerating it teaches nothing.
 */
const MAX_NOTE_NAMES = 6;

/**
 * The chord's distinct note names, from the lowest up: `"Si · Mi · Sol♯"`.
 * `null` when there is nothing worth listing.
 *
 * Used when the keyboard is too wide to carry labels on the keys — the
 * names are the point, so they move rather than disappear. Octave numbers
 * are left out: at this stage "which key" is answered by the drawing, and
 * `Mi3` would be reading notation, which is what Virtuosa avoids.
 *
 * ## Why it deduplicates, and why it gives up
 *
 * `entry.midis` is every midi *sounding* in the window, so on a chord held
 * across two bars under a melody it is long, and dropping the octave turns
 * it into repeats: one measured window produced 15 labels for 7 classes,
 * `Mi` and `La` three times each. A beginner reads a row of note names left
 * to right as an order to play them. That row was neither an order — it is
 * sorted by pitch, not by time — nor a set. It was not true of anything.
 *
 * Deduplicating makes it a set; the caller states the pitch order in words,
 * because a row of names cannot state it on its own. And past
 * `MAX_NOTE_NAMES` the honest answer is silence: this module would rather
 * say nothing than let an enumeration pass for an explanation. Silence is
 * free here in a way it is not on the keyboard — a missing sentence reads
 * as modesty, a missing drawing would read as "this hand does not play".
 */
export function chordNoteNames(
  entry: PhraseChord,
  notation: NoteNotation,
): string[] | null {
  const seen = new Set<number>();
  const names: string[] = [];

  // `entry.midis` is ascending, so keeping each class's first occurrence
  // leaves the names in the order the ear meets them going up.
  for (const midi of entry.midis) {
    const pitchClass = ((midi % 12) + 12) % 12;

    if (seen.has(pitchClass)) {
      continue;
    }

    seen.add(pitchClass);
    names.push(noteLabel(PITCH_CLASS_NAMES[pitchClass], notation));
  }

  if (names.length === 0 || names.length > MAX_NOTE_NAMES) {
    return null;
  }

  return names;
}
